/**
 * 集點 Vuex Module
 * 管理集點相關狀態
 */
import { getRewardCardList, getRewardPointProductsList, getMemberTotalRewardPoints } from '@/services/api.service';
import moment from 'moment';

const state = {
    rewardCard: null,       // 集點卡資訊
    exchangeProducts: [],   // 可兌換商品列表（包含 product_id 和 points）
    memberTotalPoints: 0,   // 會員總點數
    loading: false,         // 載入狀態
    error: null            // 錯誤訊息
};

const getters = {
    // 當前點數（從會員總點數取得）
    currentPoints: (state) => state.memberTotalPoints,

    // 活動資訊（轉換為 UI 需要的格式）
    campaignInfo: (state) => {
        if (!state.rewardCard) return null;

        const card = state.rewardCard;

        // 如果活動未啟用，回傳 null 以顯示空狀態
        if (!card.is_actived) return null;

        return {
            campaignName: card.name ?? '集點活動',
            campaignType: card.mode === 1 ? '滿額集點' : '特定商品集點',
            mode: card.mode, // 活動模式（1=滿額集點, 2=特定商品集點）
            startDate: card.begin_datetime?.split(/[T ]/)[0]?.replace(/-/g, '/') ?? '',
            endDate: card.end_datetime?.split(/[T ]/)[0]?.replace(/-/g, '/') ?? '',
            description: card.description ?? '',
            notice: '活動結束後，本次集點將清空，請把握時間兌換商品。本店保留修改、變更、暫停或終止本活動內容之權利。',
            isActive: card.is_actived ?? false
        };
    },

    // 可兌換商品列表（原始資料）
    exchangeProducts: (state) => state.exchangeProducts,

    // 是否有可兌換商品
    hasProducts: (state) => state.exchangeProducts.length > 0,

    // 是否載入中
    isLoading: (state) => state.loading,

    // 錯誤訊息
    errorMessage: (state) => state.error
};

const mutations = {
    // 設定集點卡資訊
    SET_REWARD_CARD(state, card) {
        state.rewardCard = card;
    },

    // 設定可兌換商品
    SET_EXCHANGE_PRODUCTS(state, products) {
        state.exchangeProducts = products;
    },

    // 設定會員總點數
    SET_MEMBER_TOTAL_POINTS(state, points) {
        state.memberTotalPoints = points;
    },

    // 設定載入狀態
    SET_LOADING(state, loading) {
        state.loading = loading;
    },

    // 設定錯誤訊息
    SET_ERROR(state, error) {
        state.error = error;
    },

    // 清空錯誤
    CLEAR_ERROR(state) {
        state.error = null;
    },

    // 清空所有資料
    CLEAR_ALL_DATA(state) {
        state.rewardCard = null;
        state.exchangeProducts = [];
        state.memberTotalPoints = 0;
        state.loading = false;
        state.error = null;
    }
};

const actions = {
    /**
     * 獲取集點卡資訊
     */
    async fetchPointInfo({ commit }) {
        commit('SET_LOADING', true);
        commit('CLEAR_ERROR');

        try {
            const response = await getRewardCardList({
                page: 0,
                page_size: 1
            });

            // 檢查回應格式並取得第一筆資料
            const card = response?.data?.data?.reward_card_list?.[0] ?? null;
            commit('SET_REWARD_CARD', card);
        } catch (error) {
            console.error('獲取集點卡活動失敗:', error);
            commit('SET_ERROR', error.message || '獲取集點資訊失敗');
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    /**
     * 獲取可兌換商品列表
     */
    async fetchExchangeProducts({ commit }) {
        commit('SET_LOADING', true);
        commit('CLEAR_ERROR');

        try {
            const response = await getRewardPointProductsList({
                page: 0,
                page_size: 999
            });

            // 檢查回應格式
            const products = response?.data?.data?.reward_point_products_list ?? [];
            commit('SET_EXCHANGE_PRODUCTS', products);
        } catch (error) {
            console.error('獲取兌換商品列表失敗:', error);
            commit('SET_ERROR', error.message || '獲取商品列表失敗');
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    /**
     * 獲取會員總點數
     */
    async fetchMemberTotalPoints({ commit, rootState, state }) {
        commit('SET_LOADING', true);
        commit('CLEAR_ERROR');

        try {
            // 從 rootState 取得 store_member_uuid
            const storeMemberUuid = rootState.userData?.storeData?.store_member_uuid ?? '';

            if (!storeMemberUuid) {
                console.warn('無法取得 store_member_uuid');
                commit('SET_MEMBER_TOTAL_POINTS', 0);
                return;
            }

            const response = await getMemberTotalRewardPoints({
                store_member_uuid: storeMemberUuid
            });

            // 取得 API 回應資料
            const pointsInfo = response?.data?.data?.member_total_points_info ?? {};
            const totalPoints = pointsInfo.member_total_reward_points ?? 0;
            const lastestUpdatedAt = pointsInfo.lastest_updated_at;

            // 取得活動開始時間
            const campaignStartDate = state.rewardCard?.begin_datetime;

            // 判斷是否顯示點數
            let displayPoints = totalPoints;

            // 如果 lastest_updated_at 為 null，顯示 0
            if (!lastestUpdatedAt) {
                displayPoints = 0;
            }
            // 如果 lastest_updated_at 早於活動開始時間，顯示 0
            else if (campaignStartDate) {
                // 時區處理說明：
                // 1. begin_datetime 格式：'2026-03-01T00:00:00' → 以後端回傳時間為準，不做時區轉換
                // 2. lastest_updated_at 格式：'2026-03-03 09:46:55' → 需要 +8 小時後再比較
                //
                // 處理邏輯：
                // - begin_datetime: 直接使用 moment() 解析（會被當作本地時間，符合需求）
                // - lastest_updated_at: 使用 moment() 解析後加 8 小時

                // 處理活動開始時間（以後端回傳時間為準）
                const startTime = moment(campaignStartDate).valueOf();

                // 處理會員更新時間（需要 +8 小時）
                const updatedTime = moment(lastestUpdatedAt).add(8, 'hours').valueOf();

                if (updatedTime < startTime) {
                    displayPoints = 0;
                }
            }

            commit('SET_MEMBER_TOTAL_POINTS', displayPoints);
        } catch (error) {
            console.error('獲取會員總點數失敗:', error);
            commit('SET_ERROR', error.message || '獲取會員點數失敗');
            commit('SET_MEMBER_TOTAL_POINTS', 0);
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    /**
     * 清空集點相關資料
     */
    clearPointData({ commit }) {
        commit('CLEAR_ALL_DATA');
        console.log('集點資料已清空');
    }
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
